<!--
 * @Author: rossdong rossdong@yntengyun.com
 * @Date: 2026-03-18
 * @LastEditors: rossdong rossdong@yntengyun.com
 * @LastEditTime: 2026-04-16 14:25:10
 * @FilePath: /Mobile/src/pages/complaint/views/visual/disposalAnalysis/comps/SatisfactionCell.vue
 * @Description: 处置情况分析-处置满意度（图表 + 评价类型切换 + 统计说明）
 *
-->
<template>
    <div class="w-full flex flex-col">
        <div class="w-full flex items-end justify-between my-16">
            <div class="text-16 font-bold text-black-primary">处置满意度</div>
            <div class="flex items-center" @click="ruleDialogShow = true">
                <img src="@/assets/complaint/ic_info_tips.png" alt="" class="size-14 mr-3">
                <span>统计说明</span>
            </div>
        </div>

        <div class="card flex flex-col">
            <div class="card-top">
                <div class="type-trigger" @click="typeSheetShow = true">
                    <span class="ellipsis">{{ currentTypeLabel }}</span>
                    <van-icon name="arrow-down" class="ml-6" />
                </div>
            </div>

            <div v-if="loading" class="state-text">加载中...</div>
            <div v-else>
                <div ref="chartEl" class="w-full h-202"></div>

                <div class="mx-32">
                    <div class="legend-row">
                        <div class="legend-item" v-for="item in legendTop" :key="item.key">
                            <div class="legend-line1">
                                <i class="dot" :style="{ background: item.color }"></i>
                                <span class="legend-title">{{ item.label }}</span>
                                <span class="legend-count">{{ formatNumber(item.count) }}件</span>
                            </div>
                            <div class="legend-line2">
                                <span>占比</span>
                                <span class="legend-rate">{{ formatPercent(item.ratio) }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="legend-row">
                        <div class="legend-item legend-item--single" v-for="item in legendBottom" :key="item.key">
                            <div class="legend-line1">
                                <i class="dot" :style="{ background: item.color }"></i>
                                <span class="legend-title">{{ item.label }}</span>
                                <span class="legend-count">{{ formatNumber(item.count) }}件</span>
                            </div>
                            <div class="legend-line2">
                                <span>占比</span>
                                <span class="legend-rate">{{ formatPercent(item.ratio) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <TipsDialog v-model="ruleDialogShow" title="统计说明" :content="ruleText" confirmButtonText="我知道了" />

        <van-action-sheet :value="typeSheetShow" :actions="typeActions" cancel-text="取消" @input="typeSheetShow = $event"
            @select="onSelectType" safe-area-inset-bottom get-container="body" />
    </div>
</template>

<script>
import echarts from 'echarts'
import TipsDialog from '../../comps/TipsDialog.vue'
import APIS from "@/service/modules/complaintApis/modules/visualization.js";
import visualFilterMixin from "../../mixins/visualFilterMixin";

function fmtNumber(n) {
    const num = Number(n)
    if (!Number.isFinite(num)) return '0'
    return String(Math.round(num)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

function parsePercentToNumber(v) {
    if (v === undefined || v === null || v === '') return null
    if (typeof v === 'number') return Number.isFinite(v) ? v : null
    const s = String(v).trim()
    const n = Number.parseFloat(s.endsWith('%') ? s.slice(0, -1) : s)
    return Number.isFinite(n) ? n : null
}

export default {
    name: 'SatisfactionCell',
    mixins: [visualFilterMixin],
    components: { TipsDialog },
    data() {
        return {
            chart: null,
            _onResize: null,

            // UI state
            loading: false,
            ruleDialogShow: false,
            typeSheetShow: false,
            // evaluationType: 0 / 1
            selectedType: 0,

            // Data state (fill from API)
            apiTotal: 0,
            satisfiedRatio: '',
            scoreList: [],

            // Tips text (you can replace)
            ruleText: "1.仅统计已完成评价的投诉件；\n2.满意率=（非常满意+满意）/总计*100%\n3.拆单到不同区域，且各区域游客评价不一致的情形，统计时按评价较好的统计。例如，昆明市拆单到官渡区和五华区，官渡区游客评价“满意”，五华区游客评价“非常满意”，则统计昆明市满意度时按“非常满意”统计。"
        }
    },
    computed: {
        typeActions() {
            return [
                { name: '全部评价(含系统评价)', value: 0 },
                { name: '仅游客评价', value: 1 }
            ]
        },
        currentTypeLabel() {
            const hit = this.typeActions.find(a => Number(a.value) === Number(this.selectedType))
            return hit ? hit.name : '全部评价(含系统评价)'
        },
        orderedList() {
            const list = Array.isArray(this.scoreList) ? this.scoreList.slice() : []
            // Prefer stable order: 5(非常满意) -> 3(满意) -> 1(不满意)
            return list.sort((a, b) => Number(b.score || 0) - Number(a.score || 0))
        },
        displayTotal() {
            const t = Number(this.apiTotal || 0)
            if (t > 0) return t
            return this.orderedList.reduce((sum, it) => sum + Number(it.total || 0), 0)
        },
        displaySatisfiedRatioText() {
            if (this.satisfiedRatio) return this.satisfiedRatio
            const total = this.orderedList.reduce((sum, it) => sum + Number(it.total || 0), 0)
            if (!total) return '0%'
            // satisfied = score >= 3
            const satisfied = this.orderedList.reduce((sum, it) => {
                const score = Number(it.score || 0)
                return sum + (score >= 3 ? Number(it.total || 0) : 0)
            }, 0)
            return `${((satisfied / total) * 100).toFixed(2)}%`
        },
        legendItems() {
            const colorMap = {
                5: '#36B374', // 非常满意
                3: '#4083E7', // 满意
                1: '#FF8C19' // 不满意
            }
            return this.orderedList.map((it) => ({
                key: String(it.score ?? it.name ?? Math.random()),
                label: it.name ?? '',
                count: Number(it.total || 0),
                ratio: it.ratio ?? '',
                color: colorMap[Number(it.score)] || '#8b8f97'
            }))
        },
        legendTop() {
            return this.legendItems.slice(0, 2)
        },
        legendBottom() {
            return this.legendItems.slice(2, 3)
        }
    },
    watch: {
        selectedType() {
            this.onLoad()
        },
        scoreList: {
            deep: true,
            handler() {
                this.$nextTick(() => this.renderChart())
            }
        },
        apiTotal() {
            this.$nextTick(() => this.renderChart())
        },
        satisfiedRatio() {
            this.$nextTick(() => this.renderChart())
        }
    },
    mounted() {
        this.ensureChart()
    },
    beforeDestroy() {
        this.disposeChart()
    },
    methods: {
        formatNumber(n) {
            return fmtNumber(n)
        },
        formatPercent(v) {
            const n = parsePercentToNumber(v)
            if (n === null) return '0%'
            return `${Math.round(n)}%`
        },
        onSelectType(action) {
            this.selectedType = Number(action.value)
            this.typeSheetShow = false
        },
        ensureChart() {
            const el = this.$refs.chartEl
            if (!el) return
            if (this.chart) {
                if (typeof this.chart.getDom === 'function' && this.chart.getDom() !== el) this.disposeChart()
                else return
            }
            this.chart = echarts.init(el)
            this._onResize = () => {
                if (this.chart) this.chart.resize()
            }
            window.addEventListener('resize', this._onResize, { passive: true })
        },
        disposeChart() {
            if (this._onResize) {
                window.removeEventListener('resize', this._onResize)
                this._onResize = null
            }
            if (this.chart) {
                this.chart.dispose()
                this.chart = null
            }
        },
        buildOption() {
            const list = this.orderedList
            const total = Math.max(list.reduce((sum, it) => sum + Number(it.total || 0), 0), 1)

            const colorMap = {
                5: '#36B374',
                3: '#4083E7',
                1: '#FF8C19'
            }

            const seriesData = [
                ...list.map((it) => ({
                    value: Number(it.total || 0),
                    name: it.name ?? '',
                    itemStyle: { color: colorMap[Number(it.score)] || '#8b8f97' }
                })),
                {
                    // transparent half for the bottom
                    value: total,
                    name: '__hide__',
                    itemStyle: { color: 'transparent' },
                    label: { show: false },
                    tooltip: { show: false },
                    emphasis: { disabled: true }
                }
            ]


            const count = fmtNumber(this.displayTotal)
            const rate = this.displaySatisfiedRatioText

            return {
                backgroundColor: '#fff',
                tooltip: {
                    trigger: 'item',
                    confine: true
                },
                title: {
                    left: 'center',
                    textStyle: {
                        color: '#1E2226',
                        fontSize: 14,
                        fontWeight: 600,
                    },

                    triggerEvent: true,
                    top: '45%',
                    text: '总计已评价',
                    subtext: `{count|${count}} 件\n{secondary|满意率：${rate}}`,
                    subtextStyle: {
                        color: '#1E2226',
                        fontSize: 12,
                        align: 'center',
                        verticalAlign: 'bottom',
                        rich: {
                            count: {
                                fontSize: 24,
                                fontWeight: 700,
                                color: '#1E2226'
                            },
                            secondary: {
                                padding: [4, 0],
                                color: '#54565C'
                            }
                        }


                    },
                },
                series: [
                    {
                        name: '处置满意度',
                        type: 'pie',
                        radius: ['60%', '100%'],
                        center: ['50%', '65%'],
                        startAngle: 180,
                        endAngle: 360,

                        label: {
                            show: false,

                        },
                        labelLine: { show: false },
                        itemStyle: {
                            borderColor: '#fff',
                            borderWidth: 6
                        },
                        data: seriesData
                    }
                ]
            }
        },

        renderChart() {
            this.ensureChart()
            if (!this.chart) return
            this.chart.setOption(this.buildOption(), true)
            this.chart.resize()
        },
        applyApiData(data) {
            // data = { total, satisfiedRatio, list:[{score,total,name,ratio}] }
            this.apiTotal = Number(data?.total || 0)
            this.satisfiedRatio = String(data?.satisfiedRatio || '')
            this.scoreList = Array.isArray(data?.list) ? data.list : []
        },

        onLoad() {
            const { areaId, beginTime, endTime, rule, channel } = this.filterParams;
            const params = {
                cityId: areaId,
                county: this.county,
                startDate: beginTime,
                endDate: endTime,
                rule: rule,
                channel: channel,
                evaluationType: Number(this.selectedType)
            }

            this.loading = true;
            return APIS.evaluateCount(params).then(res => {
                let data = res.data;
                this.applyApiData(data)
            }).finally(() => {
                this.loading = false;
                this.$nextTick(() => this.renderChart())
            })
        }
    }
}


</script>

<style lang="less" scoped>
.card {
    border-radius: 12px;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.04);
    padding: 14px 12px 18px;
}

.card-top {
    display: flex;
    justify-content: flex-end;
}

.state-text {
    width: 100%;
    height: 202px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-size: 14px;
}

.type-trigger {
    max-width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    color: #1E2226;
    padding: 2px 8px;
}


.legend {
    margin-top: 6px;
}

.legend-row {
    display: flex;
    gap: 16px;
    margin-top: 10px;
}

.legend-item {
    flex: 1;
    min-width: 0;
}

.legend-item--single {
    max-width: 48%;
}

.legend-line1 {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #1E2226;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
}

.legend-title {
    flex: 0 0 auto;
}

.legend-count {
    flex: 0 0 auto;
}

.legend-line2 {
    margin-left: 18px;
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #54565C;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
}

.legend-rate {
    color: #999;
}

.dot {
    width: 12px;
    height: 12px;
    border-radius: 999px;
    display: inline-block;
}

.ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
